import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { allRemoteSettingsSelect } from '../redux/selectors/index';
import { SiteWide } from "./customComponents";
import Section from "./layouts/Section";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShieldIcon from "@mui/icons-material/Shield";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

function WithdrawalInfo() {
    const navigate = useNavigate();
    const site_settings = useSelector(allRemoteSettingsSelect);
    const charge = site_settings?.plans?.[0]?.mother?.withdrawalCharge ?? 5;

    const cardStyle = {
        background: "#ffffff",
        border: "1px solid rgba(59,130,246,0.08)",
        boxShadow: "0 4px 18px rgba(59,130,246,0.05)",
        borderRadius: 20,
        padding: 20,
        textAlign: "left"
    };

    const sectionTitleStyle = {
        color: "var(--text-main)",
        fontFamily: "'Syne', sans-serif",
        fontSize: 16,
        fontWeight: 700,
        margin: "0 0 16px 0",
        display: "flex",
        alignItems: "center",
        gap: 8
    };

    const itemStyle = {
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 16
    };

    const itemTitleStyle = {
        color: "var(--text-main)",
        fontSize: 13,
        fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
        margin: "0 0 2px 0"
    };

    const itemDescStyle = {
        color: "var(--text-muted)",
        fontSize: 12,
        fontFamily: "'DM Sans', sans-serif",
        margin: 0,
        lineHeight: 1.4
    };

    return (
        <SiteWide title="Withdrawal Information">
            <Section title="Withdrawal Info">
                <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>
                    
                    {/* Hero Info Banner */}
                    <div style={{
                        borderRadius: 22, padding: "24px 20px", position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 55%,#3b82f6 100%)",
                        boxShadow: "0 10px 28px rgba(37,99,235,0.3)",
                        textAlign: "center"
                    }}>
                        <div style={{
                            position: "absolute", top: -30, right: -30, width: 160, height: 160,
                            borderRadius: "50%", background: "radial-gradient(circle,rgba(147,197,253,0.22),transparent)",
                            pointerEvents: "none",
                        }} />
                        <InfoOutlinedIcon style={{ color: "#ffffff", fontSize: 36, marginBottom: 8 }} />
                        <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 800, fontFamily: "'Syne', sans-serif", margin: 0 }}>
                            Withdrawal Guidelines
                        </h3>
                        <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", margin: "6px 0 0" }}>
                            Please read our instructions before requesting a withdrawal.
                        </p>
                    </div>

                    {/* Policy Card */}
                    <div style={cardStyle}>
                        <h4 style={sectionTitleStyle}>
                            <ShieldIcon style={{ color: "#2563eb", fontSize: 18 }} />
                            Policies & Rules
                        </h4>

                        <div style={itemStyle}>
                            <div style={{
                                width: 32, height: 32, borderRadius: 10, background: "rgba(37,99,235,0.06)",
                                display: "flex", alignItems: "center", justifyContent: "center", shrink: 0
                            }}>
                                <AccessTimeIcon style={{ color: "#2563eb", fontSize: 16, margin: "auto" }} />
                            </div>
                            <div>
                                <h5 style={itemTitleStyle}>Processing Time</h5>
                                <p style={itemDescStyle}>
                                    Withdrawal requests are processed within 1 to 24 hours. Payouts are made directly to your bound account.
                                </p>
                            </div>
                        </div>

                        <div style={itemStyle}>
                            <div style={{
                                width: 32, height: 32, borderRadius: 10, background: "rgba(16,185,129,0.06)",
                                display: "flex", alignItems: "center", justifyContent: "center", shrink: 0
                            }}>
                                <AccountBalanceWalletIcon style={{ color: "#10b981", fontSize: 16, margin: "auto" }} />
                            </div>
                            <div>
                                <h5 style={itemTitleStyle}>Transaction Fee</h5>
                                <p style={itemDescStyle}>
                                    A standard withdrawal fee of {charge}% is automatically deducted from the total request amount.
                                </p>
                            </div>
                        </div>

                        <div style={itemStyle}>
                            <div style={{
                                width: 32, height: 32, borderRadius: 10, background: "rgba(245,158,11,0.06)",
                                display: "flex", alignItems: "center", justifyContent: "center", shrink: 0
                            }}>
                                <AccountBalanceIcon style={{ color: "#f59e0b", fontSize: 16, margin: "auto" }} />
                            </div>
                            <div>
                                <h5 style={itemTitleStyle}>Bound Details Required</h5>
                                <p style={itemDescStyle}>
                                    For automatic confirmations, you must bind your crypto wallet addresses. For local bank transfers, ensure your bank details are complete.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Security Notice Card */}
                    <div style={{
                        ...cardStyle,
                        background: "linear-gradient(135deg,rgba(59,130,246,0.04),rgba(59,130,246,0.01))",
                        border: "1px solid rgba(59,130,246,0.12)"
                    }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <ShieldIcon style={{ color: "#2563eb", fontSize: 20, marginTop: 2 }} />
                            <div>
                                <h5 style={{ ...itemTitleStyle, fontSize: 14 }}>Security PIN Verification</h5>
                                <p style={{ ...itemDescStyle, marginTop: 4 }}>
                                    All transactions require your 6-digit security PIN. Never share your PIN with anyone, including customer representatives.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <button
                        onClick={() => navigate("/atm")}
                        style={{
                            width: "100%", padding: "16px 0", borderRadius: 16, border: "none",
                            background: "linear-gradient(135deg,#1e3a8a,#2563eb)",
                            color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                            cursor: "pointer", outline: "none",
                            boxShadow: "0 6px 20px rgba(37,99,235,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            transition: "all 0.2s",
                        }}
                    >
                        Proceed to ATM
                    </button>

                </div>
            </Section>
        </SiteWide>
    );
}

export default WithdrawalInfo;
